/**
 * Admin: user accounts and roles, databases, and the offline queue.
 */

import { useCallback, useEffect, useState } from 'react';

import * as api from '../lib/api';
import { clearOutbox, listOutbox, type OutboxEntry } from '../lib/offline';
import { useSession } from '../lib/session';

const ROLES = [
  ['admin', 'Admin - full access'],
  ['manager', 'Manager - sell, refunds, discounts, close out'],
  ['staff', 'Staff - sell only'],
];

export default function AdminPage() {
  const { user, sync, refreshPending } = useSession();
  const [users, setUsers] = useState<api.User[]>([]);
  const [databases, setDatabases] = useState<string[]>([]);
  const [queue, setQueue] = useState<OutboxEntry[]>([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('staff');
  const [newDatabase, setNewDatabase] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.listUsers();
      setUsers(data.users);
    } catch (err) {
      setMessage((err as api.ApiError).message);
    }
    try {
      const data = await api.listDatabases();
      setDatabases(data.databases.map((entry) => entry.db_path));
    } catch {
      setDatabases([]);
    }
    setQueue(await listOutbox());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      await action();
      setMessage(success);
      await load();
      refreshPending();
    } catch (err) {
      setMessage((err as api.ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      {message && <p className="notice">{message}</p>}

      <section className="card">
        <h2>Users &amp; roles</h2>
        <div className="table-wrap">
          <table className="items">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((entry) => (
                <tr key={entry.email}>
                  <td className="mono small">{entry.email}</td>
                  <td>{entry.name}</td>
                  <td>
                    <select
                      value={entry.role}
                      disabled={busy}
                      onChange={(event) =>
                        void run(
                          () => api.updateUser(entry.email, { role: event.target.value }),
                          `${entry.email} is now ${event.target.value}.`,
                        )
                      }
                    >
                      {ROLES.map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {/* Deleting yourself would end the session mid-action, and
                        the backend refuses it anyway. */}
                    {entry.email !== user?.email && (
                      <button
                        type="button"
                        className="ghost danger"
                        disabled={busy}
                        onClick={() => {
                          if (!window.confirm(`Delete ${entry.email}?`)) return;
                          void run(
                            () => api.deleteUser(entry.email),
                            'Account deleted.',
                          );
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3>Add a user</h3>
        <div className="field-row">
          <label>
            Email
            <input value={newEmail} onChange={(event) => setNewEmail(event.target.value)} />
          </label>
          <label>
            Name
            <input value={newName} onChange={(event) => setNewName(event.target.value)} />
          </label>
          <label>
            Password
            <input
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              minLength={8}
            />
          </label>
          <label>
            Role
            <select value={newRole} onChange={(event) => setNewRole(event.target.value)}>
              {ROLES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="primary"
            disabled={busy || !newEmail.includes('@') || newPassword.length < 8}
            onClick={() =>
              void run(async () => {
                await api.createUser({
                  email: newEmail.trim(),
                  password: newPassword,
                  role: newRole,
                  name: newName.trim(),
                });
                setNewEmail('');
                setNewName('');
                setNewPassword('');
              }, 'Account created.')
            }
          >
            Add user
          </button>
        </div>
      </section>

      <section className="card">
        <h2>Databases</h2>
        <ul className="plain">
          {databases.map((name) => (
            <li key={name} className="mono small">
              {name}
            </li>
          ))}
        </ul>
        <div className="field-row">
          <label>
            New database
            <input
              value={newDatabase}
              onChange={(event) => setNewDatabase(event.target.value)}
              placeholder="databases/day2.json"
            />
          </label>
          <button
            type="button"
            disabled={busy || !newDatabase.trim()}
            onClick={() =>
              void run(async () => {
                await api.request('/databases', {
                  method: 'POST',
                  body: { db_path: newDatabase.trim() },
                });
                setNewDatabase('');
              }, 'Database created.')
            }
          >
            Create
          </button>
        </div>
      </section>

      <section className="card">
        <h2>Offline queue</h2>
        <p className="muted small">
          Writes saved on this device while offline. They replay in order when
          the connection returns.
        </p>
        <ul className="plain">
          {queue.map((entry) => (
            <li key={entry.id}>
              <strong>{entry.label}</strong>{' '}
              <span className="muted small">
                queued {new Date(entry.queuedAt).toLocaleString()}
                {entry.attempts > 0 && ` · ${entry.attempts} attempt(s)`}
                {entry.lastError && ` · ${entry.lastError}`}
              </span>
            </li>
          ))}
          {queue.length === 0 && <li className="muted">Nothing queued.</li>}
        </ul>
        <div className="field-row">
          <button
            type="button"
            disabled={busy || queue.length === 0}
            onClick={() =>
              void run(async () => {
                setMessage(await sync());
              }, 'Sync finished.')
            }
          >
            Sync now
          </button>
          <button
            type="button"
            className="ghost danger"
            disabled={busy || queue.length === 0}
            onClick={() => {
              if (
                !window.confirm(
                  'Discard every queued change? Sales that have not synced will be lost.',
                )
              ) {
                return;
              }
              void run(clearOutbox, 'Queue discarded.');
            }}
          >
            Discard queue
          </button>
        </div>
      </section>
    </div>
  );
}
